import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Stack
} from '@mui/material';
import {
  Home as HomeIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  SquareFoot as AreaIcon,
  CalendarToday as AgeIcon,
  Layers as FloorIcon,
  DirectionsWalk as DistanceIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

interface CalculatorForm {
  area: string;
  age: string;
  floor: string;
  location: string;
  lat?: number;
  lon?: number;
  rooms: string;
}

interface SimilarProperty {
  title: string;
  price: string;
  location: string;
  area: string;
  rooms: string;
  floor: string;
  age: string;
  link: string;
  similarity_score: string;
}

interface ApiResponse {
  estimated_price: string;
  properties: SimilarProperty[];
}

const MAHALLELER = [
  "15 Temmuz",
  "Birlik",
  "Çifte Havuzlar",
  "Davutpaşa",
  "Fatih",
  "Fevzi Çakmak",
  "Havaalanı",
  "Kazım Karabekir",
  "Kemer",
  "Menderes",
  "Mimar Sinan",
  "Namık Kemal",
  "Nine Hatun",
  "Oruçreis",
  "Tuna",
  "Turgut Reis",
  "Yavuz Selim"
];

const MAHALLE_KOORDINATLARI: { [key: string]: [number, number] } = {
  "15 Temmuz": [41.03624, 28.81669],
  "Birlik": [41.05019, 28.87056],
  "Çifte Havuzlar": [41.025901, 28.896925],
  "Davutpaşa": [41.03317, 28.88792],
  "Fatih": [41.05185, 28.84504],
  "Fevzi Çakmak": [41.04711, 28.85389],
  "Havaalanı": [41.056704, 28.870793],
  "Kazım Karabekir": [41.02626, 28.8475],
  "Kemer": [41.05229, 28.88042],
  "Menderes": [41.04109, 28.87694],
  "Mimar Sinan": [41.02452, 28.81605],
  "Namık Kemal": [41.050781, 28.849476],
  "Nine Hatun": [41.0370239, 28.8802419],
  "Oruçreis": [41.058995, 28.857222],
  "Tuna": [41.049285, 28.862717],
  "Turgut Reis": [41.058995, 28.857222],
  "Yavuz Selim": [41.0452, 28.8905]
};

const ValueCalculator: React.FC = () => {
  const [formData, setFormData] = useState<CalculatorForm>({
    area: '',
    age: '',
    floor: '',
    location: '',
    rooms: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    estimatedValue: string;
    futureValue: string;
    similarProperties: SimilarProperty[];
  } | null>(null);

  const handleChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value
    }));
  };

  const handleLocationSelect = (location: string) => {
    const coordinates = MAHALLE_KOORDINATLARI[location];
    if (coordinates) {
      setFormData(prev => ({
        ...prev,
        location,
        lat: coordinates[0],
        lon: coordinates[1]
      }));
    } else {
      // Eğer koordinat bulunamazsa varsayılan değerleri kullan
      setFormData(prev => ({
        ...prev,
        location,
        lat: 41.0431,  // Esenler'in merkez koordinatları
        lon: 28.8883
      }));
    }
  };

  const calculateValue = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const lat = formData.lat || 41.0431;
      const lon = formData.lon || 28.8883;

      // Önce benzer özellikteki evleri al
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL || 'https://evrim-api.vercel.app'}/properties/similar?` +
        `area=${formData.area}&` +
        `age=${formData.age}&` +
        `floor=${formData.floor}&` +
        `location=${formData.location}&` +
        `lat=${lat}&` +
        `lon=${lon}&` +
        `rooms=${formData.rooms}`
      );

      if (!response.ok) {
        throw new Error('Değer hesaplanırken bir hata oluştu. Lütfen tekrar deneyin.');
      }

      const data: ApiResponse = await response.json();
      
      if (!data.properties || data.properties.length === 0) {
        throw new Error('Benzer özellikte ev bulunamadı. Lütfen farklı kriterler deneyin.');
      }

      // Aynı evin yaşını 0 yaparak değerini hesapla
      const futureResponse = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL || 'https://evrim-api.vercel.app'}/properties/similar?` +
        `area=${formData.area}&` +
        `age=0&` + // Yaşı 0 yap
        `floor=${formData.floor}&` +
        `location=${formData.location}&` +
        `lat=${lat}&` +
        `lon=${lon}&` +
        `rooms=${formData.rooms}`
      );

      if (!futureResponse.ok) {
        console.error('Gelecek değer API yanıtı:', await futureResponse.text());
        throw new Error('Gelecek değer hesaplanırken bir hata oluştu.');
      }

      const futureData: ApiResponse = await futureResponse.json();
      console.log('Gelecek değer verisi:', futureData);

      setResult({
        estimatedValue: data.estimated_price,
        futureValue: futureData.estimated_price,
        similarProperties: data.properties
      });
    } catch (err) {
      console.error('Hata detayı:', err);
      setError(err instanceof Error ? err.message : 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: string) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      maximumFractionDigits: 0
    }).format(parseInt(price.replace(/[^0-9]/g, '')));
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Ev Bilgileri
              </Typography>
              
              <Box sx={{ display: 'grid', gap: 2 }}>
                <Box>
                  <TextField
                    fullWidth
                    label="Metrekare"
                    name="area"
                    type="number"
                    value={formData.area}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: <AreaIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Box>
                
                <Box>
                  <TextField
                    fullWidth
                    label="Bina Yaşı"
                    name="age"
                    type="number"
                    value={formData.age}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: <AgeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Box>
                
                <Box>
                  <TextField
                    fullWidth
                    label="Bulunduğu Kat"
                    name="floor"
                    type="number"
                    value={formData.floor}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: <FloorIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Box>

                <Box>
                  <FormControl fullWidth>
                    <InputLabel>Oda Sayısı</InputLabel>
                    <Select
                      value={formData.rooms}
                      label="Oda Sayısı"
                      name="rooms"
                      onChange={(e) => setFormData(prev => ({ ...prev, rooms: e.target.value as string }))}
                    >
                      <MenuItem value="1+0">1+0</MenuItem>
                      <MenuItem value="1+1">1+1</MenuItem>
                      <MenuItem value="2+1">2+1</MenuItem>
                      <MenuItem value="3+1">3+1</MenuItem>
                      <MenuItem value="4+1">4+1</MenuItem>
                      <MenuItem value="5+">5+ ve üzeri</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                
                <Box>
                  <FormControl fullWidth>
                    <InputLabel>Mahalle</InputLabel>
                    <Select
                      value={formData.location}
                      label="Mahalle"
                      onChange={(e) => handleLocationSelect(e.target.value as string)}
                      startAdornment={<LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                    >
                      {MAHALLELER.map((mahalle) => (
                        <MenuItem key={mahalle} value={mahalle}>
                          {mahalle} Mahallesi
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={calculateValue}
                    disabled={loading || !formData.area || !formData.age || !formData.floor || !formData.location || !formData.rooms}
                    startIcon={loading ? <CircularProgress size={20} /> : <MoneyIcon />}
                  >
                    {loading ? 'Hesaplanıyor...' : 'Değer Hesapla'}
                  </Button>
                </Box>
              </Box>
            </Box>
            
            <Box sx={{ p: 2 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              {result && (
                <Box>
                  <Paper elevation={3} sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
                    <Typography variant="h6" gutterBottom>
                      Tahmini Değer
                    </Typography>
                    <Typography variant="h4" gutterBottom>
                      {formatPrice(result.estimatedValue)}
                    </Typography>
                    <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.2)' }} />
                    <Typography variant="h6" gutterBottom>
                      Kentsel Dönüşüm Sonrası Tahmini Değer
                    </Typography>
                    <Typography variant="h4" gutterBottom>
                      {result.futureValue}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                      * Kentsel dönüşüm sonrası değer artışı tahmini olup, gerçek değerden farklılık gösterebilir.
                    </Typography>
                  </Paper>
                  
                  <Typography variant="h6" gutterBottom>
                    Benzer Evler
                  </Typography>
                  
                  <List>
                    {result.similarProperties.map((property, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemIcon>
                            <HomeIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box>
                                <Typography variant="subtitle1" component="div">
                                  {property.title}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                                  <Chip
                                    icon={<MoneyIcon />}
                                    label={property.price}
                                    size="small"
                                    color="primary"
                                  />
                                  <Chip
                                    icon={<AreaIcon />}
                                    label={property.area}
                                    size="small"
                                  />
                                  <Chip
                                    icon={<TrendingUpIcon />}
                                    label={`%${property.similarity_score} Benzerlik`}
                                    size="small"
                                    color="success"
                                  />
                                </Stack>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {property.location} • {property.rooms} • {property.floor} • {property.age} yaşında
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < result.similarProperties.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ValueCalculator; 